import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo } from 'react'

import { useMyMonitoringGetSiteEventsInfinite } from '@apps-common/apis/my-monitoring/site'
import { NoResultsCard } from '@apps-common/components/common'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn, motionFadeInOnly } from '@apps-common/config/motion-variants'
import { useMobileModalAnimationSwitcher } from '@apps-common/hooks/useMobileModalAnimationSwitcher'
import { invalidateSiteEventQueries } from '@apps-common/lib/invalidateSiteEventQueries'
import {
  modalRightSlideInAnimation,
  modalRightSlideOutAnimation,
  modalSlideUpEnterAnimation,
  modalSlideUpLeaveAnimation
} from '@apps-common/lib/modal-animations'
import { IMMSiteModel } from '@apps-common/types/my-monitoring'
import { SiteEventItem, SiteEventItemSkeleton } from './SiteEventItem'
import { SiteEventsModal } from './SiteEventsModal'
import { SITE_EVENTS_CARD_TAKE } from './site-events-constants'

const DEFAULT_TAKE = SITE_EVENTS_CARD_TAKE

interface IProps {
  siteLoading?: boolean
  site?: IMMSiteModel
  registerRefetch: (caller: { id: string; refetch: () => void }) => void
  maxHeight?: number
}

export const SiteEvents: React.FC<IProps> = ({ siteLoading, site, registerRefetch, maxHeight }) => {
  const { isLoading, isError, data, hasNextPage, refetch } = useMyMonitoringGetSiteEventsInfinite({
    config: { enabled: !!site },
    identifiers: `${site?.ipAddress},${site?.imei}`,
    siteId: site?.unitId || '',
    take: DEFAULT_TAKE // DEFAULT_SITE_EVENTS_TAKE
  })

  useEffect(() => {
    registerRefetch({ id: 'siteEvents', refetch })
  }, [])

  useEffect(() => {
    if (!site?.unitId) return

    const refreshOnResume = () => {
      invalidateSiteEventQueries(site.unitId)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshOnResume()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)

    const removeNativeListeners: Array<() => void> = []
    let disposed = false

    if (Capacitor.isNativePlatform()) {
      App.addListener('resume', refreshOnResume).then((handle) => {
        if (disposed) {
          handle.remove()

          return
        }

        removeNativeListeners.push(() => handle.remove())
      })

      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          refreshOnResume()
        }
      }).then((handle) => {
        if (disposed) {
          handle.remove()

          return
        }

        removeNativeListeners.push(() => handle.remove())
      })
    }

    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
      removeNativeListeners.forEach((removeNativeListener) => removeNativeListener())
    }
  }, [site?.unitId])

  const animations = useMobileModalAnimationSwitcher({
    mobile: {
      enterAnimation: modalSlideUpEnterAnimation,
      leaveAnimation: modalSlideUpLeaveAnimation
    },
    desktop: {
      enterAnimation: modalRightSlideInAnimation,
      leaveAnimation: modalRightSlideOutAnimation
    }
  })

  const allData = useMemo(() => data?.pages?.reduce((acc, curr) => [...acc, ...curr], []) || [], [data])

  const loading = siteLoading || !data || isLoading || isError
  const containerStyle = maxHeight ? { height: maxHeight, maxHeight } : undefined

  return (
    <div className='h-full min-h-0 md:h-auto md:self-start' style={containerStyle}>
      <AnimatePresence>
        <ModalButton
          component={SiteEventsModal}
          disabled={loading}
          componentProps={{
            site
          }}
          enterAnimation={animations.enterAnimation}
          leaveAnimation={animations.leaveAnimation}
          backdropDismiss
          classes='block w-full h-full min-h-0 text-left !opacity-100'
        >
          <motion.div className='relative h-full min-h-0 overflow-hidden rounded-[35px]'>
            <div className='relative sm:bg-ctx-surface-2 sm:border sm:border-ctx-border w-full h-full min-h-0 sm:px-5 lg:px-6 py-4 rounded-[35px] overflow-hidden scrollbar-none'>
              <div className='flex items-center justify-between absolute top-0 left-0 z-[2] w-full px-2 sm:px-6 pt-3 pb-2 sm:bg-ctx-surface/10 sm:backdrop-blur sm:rounded-t-[35px] sm:border-x sm:border-t sm:border-ctx-border !border-b-none'>
                <SectionTitle classes='text-ctx-text' text='Events' />
                <ButtonRoundSm type='div' className='bg-ctx-mm-brand text-ctx-on-mm-brand' disabled={loading}>
                  <commonIcons.arrowAngled />
                </ButtonRoundSm>
              </div>
              <div className='h-full min-h-0 overflow-hidden pt-[45px] pb-[54px]'>
                <div className='flex min-h-[200px] flex-col gap-[10px]'>
                  <AnimatePresence mode='wait'>
                    {loading && (
                      <>
                        {Array.from(Array(SITE_EVENTS_CARD_TAKE).keys()).map((k, i) => (
                          <motion.div key={`site-event-item-skeleton-${k}-${i}`} {...motionFadeIn(0.2)}>
                            <SiteEventItemSkeleton />
                          </motion.div>
                        ))}
                        <div className='mx-auto bg-skeleton rounded-full w-[105px] h-[26px] animate-pulse' />
                      </>
                    )}
                    {!loading &&
                      allData?.slice(0, SITE_EVENTS_CARD_TAKE)?.map((d, i) => (
                        <motion.div key={`site-event-item-${d.id}-${i}`} {...motionFadeIn(0.2)}>
                          <SiteEventItem data={d} containerClasses='!bg-ctx-surface' />
                        </motion.div>
                      ))}
                    {!loading && allData.length === 0 && (
                      <motion.div key={`site-no-events`} {...motionFadeIn(0.2)} className='flex items-center justify-center pt-2'>
                        <NoResultsCard classes='bg-ctx-surface w-full'>This site currently has no events to display.</NoResultsCard>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {!loading && hasNextPage && (
                  <>
                    <div className='pointer-events-none absolute bottom-0 left-0 z-[2] h-[105px] w-full bg-gradient-to-b from-ctx-bg/0 via-ctx-bg/80 to-ctx-bg sm:from-ctx-surface-2/0 sm:via-ctx-surface-2/85 sm:to-ctx-surface-2' />
                    <motion.div
                      key='site-events-more-button'
                      {...motionFadeInOnly(0.3, 0.5)}
                      className='absolute bottom-4 left-1/2 z-[3] -translate-x-1/2 bg-ctx-mm-brand text-ctx-on-mm-brand text-[0.85rem] flex items-center justify-center gap-[10px] rounded-full pl-[15px] pr-[7px] py-[5px] shadow-[0_10px_25px_hsl(var(--rn-ctx-bg,var(--rn-bg))/0.28)]'
                    >
                      More Events
                      <div className='rounded-full bg-ctx-mm-brand/40 p-[2px]'>
                        <commonIcons.arrowAngled />
                      </div>
                    </motion.div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </ModalButton>
      </AnimatePresence>
    </div>
  )
}
